import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHeader from "../components/SectionHeader";
import { useLanguage } from "../contexts/LanguageContext";
import { IMAGES, CONTACT } from "../data/site";
import { postContact } from "../lib/api";

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-body placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] transition";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-neutral-700 font-heading">
        {label} {required && <span className="text-[#B5651D]">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function Contact() {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await postContact(form);
      toast.success(res.message || t.contact.successMessage);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow={t.contact.eyebrow}
        title={t.contact.title}
        subtitle={t.contact.subtitle}
        image={IMAGES.funeral}
      />

      <section className="py-20">
        <div className="gk-container grid lg:grid-cols-12 gap-10">
          {/* CONTACT INFO */}
          <div className="lg:col-span-5">
            <SectionHeader eyebrow={t.contact.eyebrow} title={t.contact.title} />
            <p className="mt-5 text-neutral-700">{t.contact.subtitle}</p>

            <div className="mt-10 space-y-5">
              <ContactLine icon={Mail} label={t.contact.email} value={CONTACT.email} href={`mailto:${CONTACT.email}`} testId="contact-info-email" />
              <ContactLine icon={Phone} label={t.contact.phone} value={CONTACT.phone1} href={`tel:${CONTACT.phone1.replace(/[^+\d]/g, "")}`} testId="contact-info-phone-1" />
              <ContactLine icon={Phone} label={t.contact.phone} value={CONTACT.phone2} href={`tel:${CONTACT.phone2.replace(/[^+\d]/g, "")}`} testId="contact-info-phone-2" />
              <ContactLine icon={MapPin} label={t.contact.office} value={t.contact.officeValue} testId="contact-info-office" />
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-[2rem] border border-black/5 shadow-xl p-8 sm:p-10" data-testid="contact-form-card">
              <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t.contact.fields.name} required>
                    <input type="text" required value={form.name} onChange={update("name")} className={inputCls} data-testid="contact-input-name" />
                  </Field>
                  <Field label={t.contact.fields.email} required>
                    <input type="email" required value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-input-email" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t.contact.fields.phone}>
                    <input type="tel" value={form.phone} onChange={update("phone")} className={inputCls} data-testid="contact-input-phone" />
                  </Field>
                  <Field label={t.contact.fields.subject}>
                    <input type="text" value={form.subject} onChange={update("subject")} className={inputCls} data-testid="contact-input-subject" />
                  </Field>
                </div>
                <Field label={t.contact.fields.message} required>
                  <textarea rows={6} required value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} data-testid="contact-input-message" />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  className="gk-btn-primary justify-center disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? <><Loader2 size={18} className="animate-spin" /> {t.common.sending}</> : t.common.submit}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactLine({ icon: Icon, label, value, href, testId }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="h-12 w-12 rounded-2xl bg-[#0B5D1E]/10 text-[#0B5D1E] flex items-center justify-center flex-shrink-0">
        <Icon size={20} />
      </div>
      <div>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#B5651D]">{label}</p>
        <p className="mt-1 font-heading font-semibold text-lg text-neutral-900 break-all">{value}</p>
      </div>
    </div>
  );

  if (href) {
    return (
      <a href={href} data-testid={testId} className="block hover:-translate-y-0.5 transition-transform">
        {content}
      </a>
    );
  }
  return <div data-testid={testId}>{content}</div>;
}
